import React, { useState, useEffect } from 'react';
import {
  Tag,
  Tooltip,
  Button,
  Select,
  Input,
} from 'antd';
import DatePicker from '../../../components/DatePicker';
import { Link } from 'react-router-dom';
import { store } from '../../../app';
import './constants.scss'
import moment from 'moment';

import Icon,
{
  UserOutlined,
  TeamOutlined,
  GlobalOutlined,
  LockOutlined,
  ShrinkOutlined,
  ArrowsAltOutlined,
  MessageTwoTone,
  LinkOutlined,
  EyeFilled,
  DownloadOutlined,
  UploadOutlined,
  BellOutlined,
  BellTwoTone,
  ExclamationCircleOutlined,
  CommentOutlined,
  SyncOutlined,
  ExclamationOutlined,
  WarningOutlined,
  MessageOutlined,
  EditOutlined,
  ThunderboltTwoTone,
  SendOutlined,
  StopOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import {
  HeadCircuit,
  FolderSimplePlus,
  ListChecks,
  ClipboardText,
  ShareNetwork,
  CheckCircle,
  EnvelopeSimple,
  HourglassMedium,
} from '@phosphor-icons/react';
import { ClipboardPlus } from '../../../components/icons';
import AutoSplitIcon from '../../../components/autoSplit/AutoSplitIcon';
import { validFaxNumber, formatValidFaxNumber, validEmail } from '../../../global/validation';
import { getSentToProviderIcon, getSentToPatientIcon, getSignatureCompletedIcon } from './sent_inbox_constants';
import ReferralLinkAction from './ReferralLinkAction';
import {
  getPatientInfoStatusConfig,
  PatientInfoStatusIcon,
} from './patientInfoStatus';
import getReferralSmsDisplayConfig from './referralSmsDisplay';
import getReferralReplyDisplayConfig from './referralReplyDisplay';
import { getReferralsQuery, updateReferral, updateReferralDueDate } from '../../../actions/records';
import { openModal } from '../../../actions/modal';
import { formatDateTimeWithoutSeconds } from '../../../util/helpers/dateHelpers';
import { buildSmsTemplateMessage } from '../../../util/smsTemplateVariables';
import { buildReferralStatusSuccessMessage } from './referralStatusConsequences';
import ReferralStatusSelect from './ReferralStatusSelect';
import { captureAnalytics } from '../../../actions/preload';
import DedupBadge, { receivedDobLastNameSearchPath } from '../../../components/inbox/DedupBadge';

const privacyLevelIcon = {
  organization: { icon: <GlobalOutlined />, tooltip: 'This record is shared with the organization' },
  department: { icon: <TeamOutlined />, tooltip: 'This record is shared with your department' },
  owner: { icon: <LockOutlined />, tooltip: 'This record is private' },
};

const renderPrivacyLevel = (privacyLevel) => {
  const { icon, tooltip } = privacyLevelIcon[privacyLevel];
  return (
    <Tooltip title={tooltip} placement="left">
      {icon}
    </Tooltip>
  );
};

const renderArchivedBy = (archivedBy) => {
  return (
    <Tooltip title={`Archived by: ${archivedBy}`} placement="topRight">
      <UserOutlined style={{ verticalAlign: 'middle' }} />
    </Tooltip>
  );
};

const renderUploadedBy = (uploadedBy) => {
  return (
    <Tooltip title={`Uploaded by: ${uploadedBy}`}>
      <UploadOutlined />
    </Tooltip>
  );
};

const ReferringProviderSharePrompt = ({
  fileToken,
  referringProvider,
  providerOffice,
  senderFax,
  referralSharePromptState,
  // eslint-disable-next-line react/prop-types
  hasUnreadSharedActivity,
}) => {
  if (!fileToken || !referringProvider || !referralSharePromptState) return null;

  const onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (referralSharePromptState === 'invite') {
      store.dispatch(openModal({
        type: 'INVITE_PARTNER',
        data: {
          recordId: fileToken,
          referringProvider,
          providerOffice,
          senderFax,
        },
      }));
      return;
    }

    if (referralSharePromptState === 'share') {
      store.dispatch(openModal({
        type: 'SHARE_REFERRAL',
        data: { fileToken },
      }));
      return;
    }

    if (referralSharePromptState === 'shared') {
      store.dispatch(openModal({
        type: 'SHARED_REFERRAL_DRAWER',
        data: { fileToken },
      }));
    }
  };

  let color;
  let icon;
  let label;
  let tooltip;
  let clickable = true;

  if (referralSharePromptState === 'shared') {
    color = 'green';
    icon = <CheckCircle size={11} weight="fill" style={{ marginRight: 3, verticalAlign: '-2px' }} />;
    label = 'Shared';
    tooltip = `View referral shared with ${referringProvider}`;
  } else if (referralSharePromptState === 'pending_approval') {
    color = 'orange';
    icon = <HourglassMedium size={11} weight="bold" style={{ marginRight: 3, verticalAlign: '-2px' }} />;
    label = 'Pending Approval';
    tooltip = `${referringProvider} has signed up and is awaiting approval`;
    clickable = false;
  } else if (referralSharePromptState === 'share') {
    color = 'blue';
    icon = <ShareNetwork size={11} style={{ marginRight: 3, verticalAlign: '-2px' }} />;
    label = 'Share';
    tooltip = `Share this referral with ${referringProvider} on the portal`;
  } else if (referralSharePromptState === 'invited') {
    color = 'gold';
    icon = <EnvelopeSimple size={11} weight="bold" style={{ marginRight: 3, verticalAlign: '-2px' }} />;
    label = 'Invited';
    tooltip = `Invitation sent to ${referringProvider}`;
    clickable = false;
  } else {
    color = 'purple';
    icon = <SendOutlined style={{ fontSize: 10, marginRight: 3 }} />;
    label = 'Invite';
    tooltip = `Invite ${referringProvider} to the referral portal`;
  }

  const showUnreadDot = referralSharePromptState === 'shared' && hasUnreadSharedActivity;

  return (
    <>
      <Tooltip title={tooltip} placement="topLeft">
        <Tag
          className="referral-share-tag"
          color={color}
          onClick={clickable ? onClick : undefined}
          style={{ cursor: clickable ? 'pointer' : 'default', marginLeft: 8 }}
        >
          {icon}
          {label}
        </Tag>
      </Tooltip>
      {showUnreadDot && (
        <span className="referral-share-tag__unread-dot" aria-label="New activity" />
      )}
    </>
  );
};

const recordViewerPath = (viaChromeExtension, fileToken) => {
  const path = viaChromeExtension ? `/app/chrome/view/${fileToken}` : `/app/view/${fileToken}`;
  return path;
};

const renderMerged = () => (
  <Tooltip title="This record was created by merging multiple records">
    <ShrinkOutlined style={{ marginLeft: '5px' }} />
  </Tooltip>
);

const renderSplitted = () => (
  <Tooltip title="This record was created by splitting a record">
    <ArrowsAltOutlined style={{ marginLeft: '5px' }} />
  </Tooltip>
);

/**
 * Formats appointment information into a readable string.
 * Takes a referralScheduledAppointment object and returns a formatted string
 * describing the appointment details including provider, date, time, and location.
 * Returns a default message if no appointment details are available.
 * @param {Object} referralScheduledAppointment - The appointment object
 * @returns {string} Formatted appointment string
 */
function formatAppointmentTitle(referralScheduledAppointment) {
  // Extract data with defaults
  const {
    provider_name: provider = '',
    appointment_location: location = '',
    appointment_time: dateTimeStr = ''
  } = referralScheduledAppointment || {};
  
  // Check if we have meaningful appointment data
  const hasAppointmentData = provider || location || dateTimeStr;
  if (!hasAppointmentData) {
    return 'Referral has a scheduled appointment';
  }
  // Format date and time if available
  let dateTimeFormatted = '';
  if (dateTimeStr) {
    const dateTimeObj = new Date(dateTimeStr);
    const date = dateTimeObj.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
    const time = dateTimeObj.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true
    });
    
    dateTimeFormatted = `on ${date} at ${time}`;
  }

  // Assemble the string parts
  let result = 'Appointment ';
  
  if (provider) result += `with ${provider} `;
  if (dateTimeFormatted) result += dateTimeFormatted + ' ';
  if (location) {
    result += (provider || dateTimeFormatted) ? `, ${location} ` : `at ${location} `;
  }
  result += 'has been scheduled.';

  return result;
}

const sharedColumnTitles = (fromPage, viaChromeExtension = false) => (
  [
    {
      title: 'Title',
      className: 'title',
      dataIndex: 'receiver_name',
      render: (text, record) => {
        const {
          document_title: documentTitle,
          document_category: documentCategory,
          file_token: fileToken,
          send_token: sendToken,
          slug: assignDocumentId,
          patient: isPatient,
        } = record;

        const sendTokenParam = sendToken ? `&sendToken=${sendToken}` : '';
        const assignedDocumentParam = assignDocumentId ? `&assignedDocumentId=${assignDocumentId}` : '';
        const isPatientParam = isPatient ? '&patient=true' : '';

        return (
          <>
            {record.file_token
              && (
                <span>
                  <Link
                    to={{
                      pathname: recordViewerPath(viaChromeExtension, fileToken),
                      search: `?from=${fromPage}${sendTokenParam}${assignedDocumentParam}${isPatientParam}`,
                    }}
                  >
                    <span style={{ fontWeight: '500' }}>{documentTitle}</span>
                  </Link>
                  <br />
                </span>
              )}
            {record.document_category && fromPage !== 'referred' && <span className="category-badge">{documentCategory}</span>}
          </>
        );
      },
    },
    {
      title: 'Patient Info',
      dataIndex: 'patient_name',
      className: 'patientName',
      render: (text, record) => {
        const {
          send_token: sendToken,
          file_token: fileToken,
          is_merged: isMerged,
          is_splitted: isSplitted,
          uri,
          ai_status: aiStatus,
          patient_info_edited: patientInfoEdited,
          failed_column: failedColumn,
          imported_into_emr: importedIntoEMR,
          emr_uploaded_by: emrUploadedBy,
          emr_uploaded_at: emrUploadedAt,
          auto_upload_created: autoUploadCreated,
          auto_upload_in_progress: autoUploadInProgress,
          auto_upload_scheduled: autoUploadScheduled,
          task_in_progress: taskUploadInProgress,
          task_autoupload_in_progress: taskAutoUploadInProgress,
          task_after_chart_creation_in_progress: taskAfterChartInProgress,
          task_after_chart_creation_success: taskAfterChartSuccess,
          chart_task_error: chartTaskError,
          chart_in_progress: chartInProgress,
          insurance_in_progress: insuranceInProgress,
          insurance_not_active: insuranceNotActive,
          primary_insurance_already_exists: primaryInsuranceAlreadyExists,
          chart_created: chartCreated,
          insurance_populated: insurancePopulated,
          task_created: taskCreated,
          task_autoupload: taskAutoUpload,
          emr_import_error: emrImportError,
          task_creation_error: taskCreationError,
          upload_task_error: uploadTaskError,
          chart_creation_error: chartCreationError,
          slug: assignDocumentId,
          is_patient_info_duplicate: duplicatePatientDetection,
          emr_patient_chart_link: emrPatientChartLink,
          emr_patient_chart_link_insurance: emrPatientChartLinkInsurance,
          insurance_error: insuranceError,
          referral_scheduled_appointment: referralScheduledAppointment,
          task_insurance_in_progress: taskInsuranceInProgress,
          task_creation_failed_and_insurance_error: taskCreationFailedAndInsuranceError,
          insurance_populated_and_task_created: insurancePopulatedAndTaskCreated,
          insurance_populated_and_task_creation_error: insurancePopulatedAndTaskCreationError,
          insurance_failed_and_task_created: insuranceFailedAndTaskCreated,
          patient: isPatient,
        } = record;
        let showAiIcon = false;
        if (aiStatus === 'failed') {
          showAiIcon = fromPage === 'received' && !patientInfoEdited && failedColumn !== 'both';
        } else if (aiStatus === 'successful') {
          showAiIcon = fromPage === 'received' && !patientInfoEdited;
        }
        const clickToView = 'Click here to view Patient Chart';

        const sendTokenParam = sendToken
          ? `&sendToken=${sendToken}`
          : '';

        const assignedDocumentParam = assignDocumentId ? `&assignedDocumentId=${assignDocumentId}` : '';
        const patientInfoStatus = getPatientInfoStatusConfig(record);

        let textElement;
        if (patientInfoStatus) {
          textElement = <PatientInfoStatusIcon status={patientInfoStatus} />;
        } else if (taskCreated) {
          let message = `Task for this record was successfully created in the EMR on ${emrUploadedAt}.`;
          if (emrUploadedBy) {
            message = `${emrUploadedBy} has created a task for this record on ${emrUploadedAt}.`;
          }
          textElement = <Tooltip title={message}><ListChecks size={18} color="#52c41a" style={{ marginRight: 4 }} /></Tooltip>;
        } else if (taskAutoUpload) {
          if (emrPatientChartLink) {
            let message = `This record has successfully synced into the Patient's chart and a task was created for it on ${emrUploadedAt}.`;
            if (emrUploadedBy) {
              message = `${emrUploadedBy} has successfully synced this record into the Patient's chart and a task was created for it on ${emrUploadedAt}.`;
            }
            textElement = (
              <Tooltip title={
                <span>
                  {message}
                  <br />
                  <br />
                  Click here to view Patient Chart.
                </span>
              }>
                <ClipboardPlus size={18} color="#52c41a" style={{ marginRight: 4 }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            let message = `This record has successfully synced into the Patient's chart and a task was created for it on ${emrUploadedAt}.`;
            if (emrUploadedBy) {
              message = `${emrUploadedBy} has successfully synced this record into the Patient's chart and a task was created for it on ${emrUploadedAt}.`;
            }
            textElement = <Tooltip title={message}><ClipboardPlus size={18} color="#52c41a" style={{ marginRight: 4 }} /></Tooltip>;
          }
        } else if (insurancePopulatedAndTaskCreated) {
          let message = `Patient chart and task were created, and insurance was successfully populated on ${emrUploadedAt}.`;
          if (emrUploadedBy) {
            message = `${emrUploadedBy} has created the patient chart and task, and successfully populated insurance on ${emrUploadedAt}.`;
          }
          if (emrPatientChartLink) {
            textElement = (
              <Tooltip title={
                <span>
                  {message}
                  <br />
                  <br />
                  Click here to view Patient Chart.
                </span>
              }>
                <ClipboardPlus size={18} color="#52c41a" style={{ marginRight: 4 }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            textElement = <Tooltip title={message}><ClipboardPlus size={18} color="#52c41a" style={{ marginRight: 4 }} /></Tooltip>;
          }
        } else if (chartCreated) {
          let message = `Patient Chart successfully created on ${emrUploadedAt}.`;
          if (emrUploadedBy) {
            message = `${emrUploadedBy} has created the patient chart on ${emrUploadedAt}.`;
          }
          if (emrPatientChartLink) {
            textElement = (
              <Tooltip title={
                <span>
                  {message}
                  <br />
                  <br />
                  Click here to view Patient Chart.
                </span>
              }>
                <FolderSimplePlus size={18} color="#52c41a" style={{ marginRight: 4 }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            textElement = <Tooltip title={message}><FolderSimplePlus size={18} color="#52c41a" style={{ marginRight: 4 }} /></Tooltip>;
          }
        } else if (autoUploadCreated) {
          if (emrPatientChartLink) {
            let message = `This record has successfully synced into the Patient's chart by Medsender AI on ${emrUploadedAt}.`;
            if (emrUploadedBy) {
              message = `${emrUploadedBy} has successfully synced this record into the Patient's chart on ${emrUploadedAt}.`;
            }
            textElement = (
              <Tooltip title={
                <span>
                  {message}
                  <br />
                  <br />
                  Click here to view Patient Chart.
                </span>
              }>
                <FolderSimplePlus size={18} color="#52c41a" style={{ marginRight: 4 }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            let message = `This record has successfully synced into the Patient's chart by Medsender AI on ${emrUploadedAt}.`;
            if (emrUploadedBy) {
              message = `${emrUploadedBy} has successfully synced this record into the Patient's chart on ${emrUploadedAt}.`;
            }
            textElement = <Tooltip title={message}><ClipboardText size={18} color="#52c41a" weight="fill" style={{ marginRight: 4 }} /></Tooltip>;
          }
        } else if (taskAfterChartSuccess && !taskInsuranceInProgress && !insuranceFailedAndTaskCreated) {
          let message = `Patient chart successfully created and task has been assigned on ${emrUploadedAt}.`;
          if (emrUploadedBy) {
            message = `${emrUploadedBy} has created the patient chart and assigned a task on ${emrUploadedAt}.`;
          }
          textElement = <Tooltip title={message}><ClipboardPlus size={18} color="#52c41a" style={{ marginRight: 4 }} /></Tooltip>;
        } else if (importedIntoEMR && !taskInsuranceInProgress && !insuranceFailedAndTaskCreated) {
          if (emrPatientChartLink && insurancePopulated) {
            let message = `This record was auto uploaded into your EMR and patient's insurance was populated successfully by Medsender AI on ${emrUploadedAt}.`;
            if (emrUploadedBy) {
              message = `${emrUploadedBy} has auto uploaded this record into your EMR and patient's insurance was populated successfully on ${emrUploadedAt}.`;
            }
            textElement = (
              <Tooltip title={
                <span>
                  {message}
                  <br />
                  <br />
                  Click here to view Patient Chart.
                </span>
              }>
                <FolderSimplePlus size={18} color="#52c41a" style={{ marginRight: 4 }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else if(emrPatientChartLink) {
            let message = `This record was auto uploaded into your EMR by Medsender AI on ${emrUploadedAt}.`;
            if (emrUploadedBy) {
              message = `${emrUploadedBy} has auto uploaded this record into your EMR on ${emrUploadedAt}.`;
            }
            textElement = (
              <Tooltip title={
                <span>
                  {message}
                  <br />
                  <br />
                  Click here to view Patient Chart.
                </span>
              }>
                <FolderSimplePlus size={18} color="#52c41a" style={{ marginRight: 4 }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            let message = `This record was auto uploaded into your EMR by Medsender AI on ${emrUploadedAt}`;
            if (emrUploadedBy) {
              message = `${emrUploadedBy} has auto uploaded this record into your EMR on ${emrUploadedAt}`;
            }
            textElement = <Tooltip title={message}><ClipboardText size={18} color="#52c41a" weight="fill" style={{ marginRight: 4 }} /></Tooltip>;
          }
        } else if (insuranceFailedAndTaskCreated) {
          if (emrPatientChartLink) {
            textElement = (
              <Tooltip title={insuranceFailedAndTaskCreated}>
                <ExclamationCircleOutlined style={{ color: '#b31c0e' }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            textElement = <Tooltip title={insuranceFailedAndTaskCreated}><ExclamationCircleOutlined style={{ color: '#b31c0e', marginRight: 4 }} /></Tooltip>;
          }
        } else if (taskCreationFailedAndInsuranceError) {
          if (emrPatientChartLink) {
            textElement = (
              <Tooltip title={`${taskCreationFailedAndInsuranceError}. Click here to view Patient Chart.`}>
                <ExclamationCircleOutlined style={{ color: '#b31c0e' }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            textElement = <Tooltip title={taskCreationFailedAndInsuranceError}><ExclamationCircleOutlined style={{ color: '#b31c0e', marginRight: 4 }} /></Tooltip>;
          }
        } else if (taskCreationError) {
          if (taskCreationError === 'This chart is restricted and the document was not uploaded.') {
            textElement =(
              <Tooltip title={taskCreationError}>
                <StopOutlined style={{ color: '#b31c0e', marginRight: 4 }} />
              </Tooltip>
            );
          } else {
            textElement = (
              <Tooltip title={taskCreationError}>
                <ListChecks size={18} color="#b31c0e" style={{ marginRight: 4 }} />
              </Tooltip>
            );
          }
        } else if (chartCreationError) {
          textElement = (
            <Tooltip title={chartCreationError}>
              <FolderSimplePlus size={18} color="#b31c0e" style={{ marginRight: 4 }} />
            </Tooltip>
          );
        } else if (uploadTaskError) {
          if (uploadTaskError === 'This chart is restricted and the document was not uploaded.') {
            textElement = (
              <Tooltip title={uploadTaskError}>
                <StopOutlined style={{ color: '#b31c0e', marginRight: 4 }} />
              </Tooltip>
            );
          } else {
            textElement = (
              <Tooltip title={uploadTaskError}>
                <ExclamationCircleOutlined style={{ color: '#b31c0e', marginRight: 4 }} />
              </Tooltip>
            );
          }
        } else if (insuranceError) {
          if (insuranceNotActive) {
            textElement = (
              <Tooltip title={`${insuranceNotActive}. ${clickToView}`}>
                <WarningOutlined style={{ color: 'orange' }} onClick={(e) => {
                  window.open(emrPatientChartLinkInsurance, '_blank', 'noopener,noreferrer');
                e.stopPropagation();
                e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else if (primaryInsuranceAlreadyExists) {
            textElement = (
              <Tooltip title={`${primaryInsuranceAlreadyExists}. ${clickToView}`}>
              <WarningOutlined style={{ color: 'orange' }} onClick={(e) => {
                window.open(emrPatientChartLinkInsurance, '_blank', 'noopener,noreferrer');
                e.stopPropagation();
                e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            textElement = (
              <Tooltip title={`${insuranceError}. ${clickToView}`}>
                <ExclamationCircleOutlined style={{ color: '#b31c0e' }} onClick={(e) => {
                  window.open(emrPatientChartLinkInsurance, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          }
        } else if (insurancePopulatedAndTaskCreationError) {
          if (emrPatientChartLink) {
            textElement = (
              <Tooltip title={`${insurancePopulatedAndTaskCreationError}. Click here to view Patient Chart.`}>
                <ExclamationCircleOutlined style={{ color: '#b31c0e' }} onClick={(e) => {
                  window.open(emrPatientChartLink, '_blank', 'noopener,noreferrer');
                  e.stopPropagation();
                  e.preventDefault();
                }}/>
              </Tooltip>
            );
          } else {
            textElement = <Tooltip title={insurancePopulatedAndTaskCreationError}><ExclamationCircleOutlined style={{ color: '#b31c0e', marginRight: 4 }} /></Tooltip>;
          }
        } else if (chartTaskError && !taskInsuranceInProgress && !insuranceFailedAndTaskCreated) {
          textElement = (
            <Tooltip title={chartTaskError}>
              <ClipboardPlus size={18} color="orange" style={{ marginRight: 4 }} />
            </Tooltip>
          );
        } else if (!importedIntoEMR && emrImportError) {
          if (emrImportError === 'AI detected multiple patients in this record.') {
            textElement = (
              <Tooltip title={emrImportError}>
                <TeamOutlined style={{ color: '#b31c0e' }} />
                &nbsp;
              </Tooltip>
            );
          } else if (emrImportError === 'Failed to find the patient chart') {
            if (localStorage.getItem('m8_org') === 'National Spine & Pain Centers') {
              textElement = (
                <Tooltip title={"Failed to find patient chart. Please check the spelling of their name and DOB, or ensure the pateint chart exists."}>
                  <ClipboardText size={18} weight="fill" color="#b31c0e" style={{ marginRight: 4 }} />
                </Tooltip>
              );
            } else {
              textElement = (
                <Tooltip title="Failed to find patient chart. Please check the spelling of their name and DOB, or use the 'Create New Chart' button if the patient doesn't exist.">
                  <ClipboardText size={18} weight="fill" color="#b31c0e" style={{ marginRight: 4 }} />
                </Tooltip>
              );
            }
          } else if (emrImportError === 'This chart is restricted and the document was not uploaded.') {
            textElement = (
              <Tooltip title={emrImportError}>
                <StopOutlined style={{ color: 'orange' }} />
                &nbsp;
              </Tooltip>
            );
          } else {
            textElement = (
              <Tooltip title={emrImportError}>
                <ExclamationCircleOutlined style={{ color: '#b31c0e' }} />
                &nbsp;
              </Tooltip>
            );
          }
        } else if (insuranceInProgress) {
          textElement = <Tooltip title={'Chart creation successful, insurance information is currently being populated in the EMR.'}><SyncOutlined spin style={{ color: 'orange', marginRight: 4 }} /></Tooltip>;
        } else if (chartInProgress) {
          textElement = <Tooltip title="Patient Chart is currently being created. Refresh the page in a few minutes to see updated status"><SyncOutlined spin style={{ color: 'orange', marginRight: 4 }} /></Tooltip>;
        } else if (autoUploadScheduled) {
          textElement = <Tooltip title="Queued for after-hours upload to the EMR. This will be sent automatically tonight."><SyncOutlined style={{ color: '#1890ff', marginRight: 4 }} /></Tooltip>;
        } else if (autoUploadInProgress) {
          textElement = <Tooltip title="The record is currently syncing into the patient's chart. Refresh the page in a few minutes to see updated status"><SyncOutlined spin style={{ color: 'orange', marginRight: 4 }} /></Tooltip>;
        } else if (taskUploadInProgress) {
          textElement = <Tooltip title="A task is currently being created in this patient's chart. Refresh the page in a few minutes to see updated status"><SyncOutlined spin style={{ color: 'orange', marginRight: 4 }} /></Tooltip>;
        } else if (taskAutoUploadInProgress) {
          textElement = <Tooltip title="The record is currently syncing into the patient's chart and a task is being created. Refresh the page in a few minutes to see updated status"><SyncOutlined spin style={{ color: 'orange', marginRight: 4 }} /></Tooltip>;
        } else if (taskInsuranceInProgress) {
          textElement = <Tooltip title="Patient chart created successfully. Task creation and insurance population are in progress. Refresh the page in a few minutes to see updated status"><SyncOutlined spin style={{ color: 'orange', marginRight: 4 }} /></Tooltip>;
        } else if (taskAfterChartInProgress) {
          textElement = <Tooltip title="Patient Chart created successfully and a task is being created. Refresh the page in a few minutes to see updated status"><SyncOutlined spin style={{ color: 'orange', marginRight: 4 }} /></Tooltip>;
        } else {
          textElement = showAiIcon && <Tooltip title="The patient information was extracted using Medsender AI"><HeadCircuit size={18} color="#691B9A" style={{ marginRight: 4 }} /></Tooltip>;
        }
        let appointmentTextElement = null;
        if (referralScheduledAppointment) {
          const title = formatAppointmentTitle(referralScheduledAppointment);
          appointmentTextElement = (
            <span style={{ marginLeft: '5px' }}>
              <Tooltip title={title}>
                <CalendarOutlined style={{ color: '#52c41a' }} />
              </Tooltip>
            </span>
          );
        }

        // Reply records (identified by uri) render as plain text (not clickable links)
        // but still show EMR status icons computed above
        if (uri && !viaChromeExtension) {
          return (
            <span>
              {textElement}
              {text}
            </span>
          );
        }

        const nationalSpineOrg = localStorage.getItem('m8_org') === 'National Spine & Pain Centers';
        const patientParam = isPatient ? '&patient=true' : '';

        return (
          <>
            <div>
              {emrPatientChartLink
                ? (
                  <div style={{ display: 'inline' }}>
                    {textElement}
                  </div>
                ) : <div />
              }
              <Link
                to={{
                  pathname: recordViewerPath(viaChromeExtension, fileToken),
                  search: `?from=${fromPage}${sendTokenParam}${assignedDocumentParam}${patientParam}`,
                }}
              >
                {text
                  ? (
                    <div style={{ display: 'inline' }}>
                      {emrPatientChartLink
                        ? (
                          <div style={{ display: 'inline' }}>
                            {text}
                            {referralScheduledAppointment && appointmentTextElement}
                          </div>
                        ) : (
                          <div style={{ display: 'inline' }}
                          onClick={(e) => {
                            const selectedText = window.getSelection().toString();
                            if (selectedText) {
                              e.stopPropagation();
                            }
                          }}
                          >
                            {textElement}
                            {text}
                            {referralScheduledAppointment && appointmentTextElement}
                          </div>
                        )}
                    </div>
                  )
                  : <span className="missing-text">{showAiIcon && <Tooltip title="The patient information was extracted using Medsender AI"><HeadCircuit size={18} color="#691B9A" style={{ marginRight: 4 }} /></Tooltip>}Not Provided</span>
                }
              </Link>
              {text && (
                <ReferralLinkAction
                  fromPage={fromPage}
                  onOpenModal={() => store.dispatch(openModal({
                    type: 'LINK_REFERRAL',
                    data: { record },
                  }))}
                  onUnlinkSuccess={() => store.dispatch(getReferralsQuery(window.location.search || '?page=1'))}
                  record={record}
                />
              )}
              <div style={{ color: 'grey' }}>
                {duplicatePatientDetection ? (
                  <div>
                    <Tooltip title="Patient with same Name and DOB detected in other records. Click here to see records.">
                      {nationalSpineOrg ? (
                        <WarningOutlined style={{ color: 'orange' }} />
                      ) : (
                        <ExclamationOutlined style={{ color: 'orange' }} />
                      )}
                      &nbsp;
                      {/* Search by DOB + last name: duplicate detection normalizes
                          names (middle initials, suffixes), so a literal full-name
                          search can miss the records that triggered this alert. DOB
                          alone surfaces unrelated same-DOB patients, so we add the
                          last name (which real duplicates always share) to cut the
                          noise while still matching every flagged record. */}
                      <a href={receivedDobLastNameSearchPath(record)} style={{ color: '#FFA500' }}>
                        {record.patient_dob}
                      </a>
                    </Tooltip>
                  </div>
                ) : record.patient_dob}
                { (fromPage === 'uploaded' || fromPage === 'referred' || fromPage === 'received') && isMerged && renderMerged()}
                { isSplitted && renderSplitted() }
                <DedupBadge
                  badge={record.task_dedup_verdict}
                  record={record}
                />
              </div>
            </div>
          </>
        );
      },
    },
  ]
);

export const sharedAssignedColumns = [
  {
    title: 'From',
    dataIndex: 'sender_email',
    render: (text, record) => (
      <>
        {record.sender_name
          && (
            <>
              <span style={{ fontWeight: '500' }}>{record.sender_name}</span>
              <br />
            </>
          )}
        {validFaxNumber(record.sender_email).valid ? formatValidFaxNumber(record.sender_email) : record.sender_email}
      </>
    ),
  },
  {
    title: 'To',
    dataIndex: 'receiver_name',
    render: (text, record) => {
      return (
        <>
          {record.receiver_name
            && (
              <span>
                <span style={{ fontWeight: '500' }}>{record.receiver_name}</span>
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                {record.organization && <span>&nbsp;({record.organization})</span>}
                <br />
              </span>
            )}
          {validFaxNumber(record.receiver).valid ? formatValidFaxNumber(record.receiver) : record.receiver}
        </>
      );
    },
  },
  {
    title: 'Labels',
    dataIndex: 'labels',
    render: (text, record) => {
      const { labels, tag } = record;
      const allDepartments = localStorage.getItem('allDepartments');
      return (
        <>
          {labels && labels.map(label => (
            <Tag style={{ margin: '5px', backgroundColor: label.color, color: label.is_dark_text ? 'black' : 'white' }}>
              {label.name}
            </Tag>
          ))
          }
          {/* show old tag here as long as it's not an email or department (for the purposes of maintaining functionality for users that used old tags) */}
          {tag && !validEmail(tag) && allDepartments.indexOf(tag) === -1
            && (
              <Tooltip
                title={
                  (
                    <>
                      This is an old tag that cannot be used as a label. Please go here
                      <Link to="/app/user?tab=manage_labels"> Manage Labels </Link>
                      to add a new label to your library
                    </>
                )}
              >
                <Tag style={{ color: 'rgb(106, 27, 154)', backgroundColor: 'white', border: '2px solid rgb(106, 27, 154)' }}>{tag}</Tag>
              </Tooltip>
            )}
        </>
      );
    },
  },
  {
    title: 'Date Received',
    dataIndex: 'date_sent',
    render: (text, record) => {
      const currentOrganizationId = store.getState().auth.currentOrganizationId;
      if (currentOrganizationId === 4254) { // Access Org. wants to see seconds
        return record.date_sent;
      }
      else {
        return formatDateTimeWithoutSeconds(record.date_sent);
      }
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (text, record) => (
      <span style={{ fontWeight: '500' }}>{record.document_status_name}</span>
    ),
  },
  {
    title: 'Assigned By',
    dataIndex: 'assigned_by',
    render: (text, record) => (
      <span style={{ fontWeight: '500' }}>{record.assigned_from}</span>
    ),
  },
  {
    title: '',
    dataindex: 'note',
    render: (text, record) => {
      return (
        <>
          {record.note
            && (
              <Tooltip title={record.note}>
                <CommentOutlined />
              </Tooltip>
            )
          }
        </>
      );
    },
  },
];

export const sharedReferredColumns = [
  {
    title: 'Referrer Info',
    dataIndex: 'sender_email',
    className: 'referrerInfo',
    render: (text, record) => (
      <>
        {record.referring_provider
          && (
            <>
              <span style={{ fontWeight: '500' }}>{record.referring_provider}</span>
              <ReferringProviderSharePrompt
                fileToken={record.file_token}
                referringProvider={record.referring_provider}
                providerOffice={record.provider_office}
                senderFax={record.sender_email}
                referralSharePromptState={record.referral_share_prompt_state}
                hasUnreadSharedActivity={record.has_unread_shared_activity}
              />
              <br />
            </>
          )}
        {record.provider_office
          && (
            <>
              <span style={{ fontWeight: '300' }}>{record.provider_office}</span>
              <br />
            </>
          )}
      </>
    ),
  },
  {
    title: 'From',
    dataIndex: 'sender_email',
    render: (text, record) => (
      <>
        {record.sender_name
          && (
            <>
              <span style={{ fontWeight: '500' }}>{record.sender_name}</span>
              <br />
            </>
          )}
        {validFaxNumber(record.sender_email).valid ? formatValidFaxNumber(record.sender_email) : record.sender_email}
      </>
    ),
  },
  {
    title: 'To',
    dataIndex: 'receiver_name',
    render: (text, record) => {
      return (
        <>
          {record.receiver_name
            && (
              <span>
                <span style={{ fontWeight: '500' }}>{record.receiver_name}</span>
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                {record.organization && <span>&nbsp;({record.organization})</span>}
                <br />
              </span>
            )}
          {validFaxNumber(record.receiver).valid ? formatValidFaxNumber(record.receiver) : record.receiver}
        </>
      );
    },
  },
  {
    title: 'Labels',
    dataIndex: 'labels',
    render: (text, record) => {
      const { labels, tag } = record;
      const allDepartments = localStorage.getItem('allDepartments');
      return (
        <>
          {labels && labels.map(label => (
            <Tag key={label.id} style={{ margin: '5px', backgroundColor: label.color, color: label.is_dark_text ? 'black' : 'white' }}>
              {label.name}
            </Tag>
          ))
          }
          {/* show old tag here as long as it's not an email or department (for the purposes of maintaining functionality for users that used old tags) */}
          {tag && !validEmail(tag) && allDepartments.indexOf(tag) === -1
            && (
              <Tooltip
                title={
                  (
                    <>
                      This is an old tag that cannot be used as a label. Please go here
                      <Link to="/app/user?tab=manage_labels"> Manage Labels </Link>
                      to add a new label to your library
                    </>
                )}
              >
                <Tag style={{ color: 'rgb(106, 27, 154)', backgroundColor: 'white', border: '2px solid rgb(106, 27, 154)' }}>{tag}</Tag>
              </Tooltip>
            )}
        </>
      );
    },
  },
  {
    title: 'Status and Due Date',
    dataIndex: 'status',
    className: 'status',
    render: (text, record) => {
      const { referral_status, referral_statuses } = record;
      const [referralStatus, setReferralStatus] = useState(referral_status ? referral_status.referral_status_id : null);
      const [currentDueDate, setCurrentDueDate] = useState(referral_status?.due_date ? moment(referral_status.due_date) : null);
      const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

      // Sync local state when the row's referral_status changes via Redux
      // (e.g. sideview UpdateReferral modal). Without this, the Select keeps
      // the stale id captured at mount and the table looks unchanged until reload.
      useEffect(() => {
        setReferralStatus(referral_status ? referral_status.referral_status_id : null);
      }, [referral_status?.referral_status_id]);
      useEffect(() => {
        setCurrentDueDate(referral_status?.due_date ? moment(referral_status.due_date) : null);
      }, [referral_status?.due_date]);

      let canChangeReferralStatus = true;
      const referralManagementStatus = localStorage.getItem('m8_referral_management');
      if (referralManagementStatus === 'false') {
        canChangeReferralStatus = false;
      }

      const handleStatusChange = async (status) => {
        if (canChangeReferralStatus) {
          const updateResponse = await store.dispatch(
            updateReferral(record.file_token, record.send_token, status, record.receiver_type)
          );
          if (updateResponse?.error) return;

          setReferralStatus(status.id);
          const currentReferralStatus = updateResponse?.data?.referred_document_status_id ||
            store.getState().records.referredDocumentStatusId;

          const linkedSmsTemplate = currentReferralStatus?.linked_sms_template;
          const linkedReplyTemplate = currentReferralStatus?.linked_reply_template;
          const patientPhoneNumber = record?.patient_phone_number;
          if (linkedSmsTemplate && !patientPhoneNumber) {
            const patientName = record.patient_name;
            const sendToken = record.send_token;
            const receiverType = record.receiver_type;
            const referringProvider = record.referring_provider;
            const messageText = buildSmsTemplateMessage({
              templateMessage: linkedSmsTemplate.message,
              patientName,
              referringProvider,
            });
            const modalType = {
              type: 'SMS_MESSAGE',
              data: {
                sendToken,
                receiverType,
                patientName,
                referringProvider,
                messageTemplates: [linkedSmsTemplate],
                messageText: messageText,
                url: linkedSmsTemplate.short_url,
                originalUrl: linkedSmsTemplate.url,
                patientPhoneNumber,
                smsMessageFeature: true,
              },
            };
            store.dispatch(openModal(modalType));

            // Notify user that auto-SMS was skipped and manual entry is needed
            toastr.info('No patient phone number on file. Enter one below to send SMS.');
          }

          if (status.auto_archive) {
            store.dispatch({
              type: 'REFERRAL_AUTO_ARCHIVED',
              payload: {
                recordId: record.send_token || record.attachment_id,
              },
            });
          }

          const successMessage = buildReferralStatusSuccessMessage({
            linkedSmsTemplate,
            linkedReplyTemplate,
            patientPhoneNumber,
            statusChanged: status.id !== referral_status?.referral_status_id,
            previousDueDate: referral_status?.due_date,
            resultingDueDate: currentReferralStatus?.due_date,
          });
          toastr.success(successMessage);
        } else {
          toastr.error("Your plan doesn't include access to update statuses for referred records. Please upgrade your plan to make any changes.");
        }
      };

      const getDueDateStatus = (date) => {
        if (!date) return 'inherit';
        const today = moment().startOf('day');
        const dueDate = moment(date).startOf('day');
        const daysUntilDue = dueDate.diff(today, 'days');

        if (daysUntilDue < 0) return 'past-due'; // Red for past due
        if (daysUntilDue <= 1) return 'almost-due'; // Yellow for same day or day before
        return 'inherit';
      };

      const updateDueDate = async (recordId, referralStatusSlug, dueDate) => {
        await store.dispatch(updateReferralDueDate(recordId, referralStatusSlug, dueDate));
      };

      return (
        <>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '200px' }}>
            <Input.Group compact>
              <ReferralStatusSelect
                value={referralStatus}
                onClick={(e) => e.stopPropagation()}
                statuses={referral_statuses || []}
                onChange={handleStatusChange}
              />
              <DatePicker
                className={`due-date-picker ${getDueDateStatus(currentDueDate)}`}
                style={{ width: '25%', paddingLeft: '2px', paddingRight: '2px' }}
                value={currentDueDate}
                format="MM/DD"
                placeholder="Due: "
                suffixIcon={<></>}
                disabledDate={current => {
                  // Can't select days before today
                  return current && current < moment().startOf('day');
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDatePickerOpen(true);
                }}
                open={isDatePickerOpen}
                onBlur={() => setIsDatePickerOpen(false)}
                onChange={async (date) => {
                  try {
                    await updateDueDate(
                      record.file_token,
                      record.referral_status.slug,
                      date?.format()
                    );
                    setCurrentDueDate(date);
                  } catch (error) {
                    console.error('Failed to update due date:', error);
                  }
                  setIsDatePickerOpen(false);
                }}
                allowClear={true}
              />
            </Input.Group>
          </div>
        </>
      );
    },
  },
  {
    title: 'No. Pages',
    dataIndex: 'num_pages',
    render: (text, record) => (
      <span style={{ fontWeight: '500' }}>{record.num_pages}</span>
    ),
  },
  {
    title: 'Date Received',
    dataIndex: 'date_sent',
    render: (text, record) => (
      formatDateTimeWithoutSeconds(record.date_sent)
    ),
  },
  {
    title: '',
    dataIndex: 'note',
    render: (text, record) => {
      const {
        referral_sms_status: referralSmsStatus,
        referral_sms_sent_at: referralSmsSentAt,
        referral_sms_sent_by: referralSmsSentBy,
        referral_reply_status: referralReplyStatus,
        has_failed_referral_replies: hasFailedReferralReplies,
        referral_reply_sent_at: referralReplySentAt,
        referral_reply_sent_by: referralReplySentBy,
        referral_reply_uri: referralReplyUri,
        sms_uri: smsUri,
      } = record;

      let referralSmsElement;
      let referralReplyElement;
      if (referralSmsStatus) {
        const { tooltipTitle, iconColor } = getReferralSmsDisplayConfig({
          ...record,
          referral_sms_status: referralSmsStatus,
          referral_sms_sent_at: referralSmsSentAt,
          referral_sms_sent_by: referralSmsSentBy,
        });

        referralSmsElement = (
          <Tooltip title={tooltipTitle} placement="topRight">
            <MessageOutlined 
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                record.getSmsData?.(smsUri);
              }}
              style={{ color: iconColor }}
            />
          </Tooltip>
        );
      }
      if (referralReplyStatus) {
        const {
          tooltipTitle,
          iconColor,
          showHistoricalFailureCorner,
        } = getReferralReplyDisplayConfig({
          referral_reply_status: referralReplyStatus,
          has_failed_referral_replies: hasFailedReferralReplies,
          referral_reply_sent_at: referralReplySentAt,
          referral_reply_sent_by: referralReplySentBy,
        });

        referralReplyElement = (
          <Tooltip title={tooltipTitle} placement="topRight">
            <button
              type="button"
              className="referral-reply-status-icon"
              aria-label={tooltipTitle}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                captureAnalytics('referral_reply_history_opened', {
                  latest_status: referralReplyStatus,
                  has_historical_failure: hasFailedReferralReplies,
                });
                record.getReferralReplyData?.(referralReplyUri);
              }}
            >
              <SendOutlined style={{ color: iconColor }} />
              {showHistoricalFailureCorner && (
                <span
                  className="referral-reply-status-icon__history-corner"
                  aria-hidden="true"
                />
              )}
            </button>
          </Tooltip>
        );
      }
      const actionRowStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        paddingTop: '5px',
        paddingBottom: '5px',
        lineHeight: 1,
      };

      return (
        <div style={actionRowStyle}>
          {record.referral_note && (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation()
                const modalType = {
                  type: 'EDIT_PATIENT',
                  data: {
                    patient: record,
                    fromPage: 'received',
                    record: record['send_token'],
                    tag: record['tag'],
                    receiverType: record['receiver_type'],
                  },
                }
                store.dispatch(openModal(modalType))
              }}
            >
              <Tooltip title={record.referral_note} placement="topLeft">
                <EditOutlined />
              </Tooltip>
            </span>
          )}
          {referralSmsStatus && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
              {smsUri && referralSmsElement}
            </span>
          )}
          {referralReplyStatus && referralReplyUri && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
              {referralReplyUri && referralReplyElement}
            </span>
          )}
        </div>
      );
    },
  },
];

export const sharedReceivedColumns = [
  {
    title: 'From',
    dataIndex: 'sender_email',
    render: (text, record) => (
      <>
        {record.sender_name
          && (
            <>
              <span style={{ fontWeight: '500' }}>{record.sender_name}</span>
              <br />
            </>
          )}
        {validFaxNumber(record.sender_email).valid ? formatValidFaxNumber(record.sender_email) : record.sender_email}
      </>
    ),
  },
  {
    title: 'To',
    dataIndex: 'receiver_name',
    render: (text, record) => {
      return (
        <>
          {record.receiver_name
            && (
              <span>
                <span style={{ fontWeight: '500' }}>{record.receiver_name}</span>
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                {record.organization && <span>&nbsp;({record.organization})</span>}
                <br />
              </span>
            )}
          {validFaxNumber(record.receiver).valid ? formatValidFaxNumber(record.receiver) : record.receiver}
        </>
      );
    },
  },
  {
    title: 'Labels',
    dataIndex: 'labels',
    render: (text, record) => {
      const { labels, tag } = record;
      const allDepartments = localStorage.getItem('allDepartments');
      return (
        <>
          {labels && labels.map(label => (
            <Tag style={{ margin: '5px', backgroundColor: label.color, color: label.is_dark_text ? 'black' : 'white' }}>
              {label.name}
            </Tag>
          ))
          }
          {/* show old tag here as long as it's not an email or department (for the purposes of maintaining functionality for users that used old tags) */}
          {tag && !validEmail(tag) && allDepartments.indexOf(tag) === -1
            && (
              <Tooltip
                title={
                  (
                    <>
                    This is an old tag that cannot be used as a label. Please go here
                      <Link to="/app/user?tab=manage_labels"> Manage Labels </Link>
                    to add a new label to your library
                    </>
                )}
              >
                <Tag style={{ color: 'rgb(106, 27, 154)', backgroundColor: 'white', border: '2px solid rgb(106, 27, 154)' }}>{tag}</Tag>
              </Tooltip>
            )}
        </>
      );
    },
  },
  {
    title: 'No. Pages',
    dataIndex: 'num_pages',
    render: (text, record) => (
      <span style={{ fontWeight: '500' }}>{record.num_pages}</span>
    ),
  },
  {
    title: 'Date Received',
    dataIndex: 'date_sent',
    render: (text, record) => {
      const currentOrganizationId = store.getState().auth.currentOrganizationId;
      if (currentOrganizationId === 4254) { // Access Org. wants to see seconds
        return record.date_sent;
      }
      else {
        return formatDateTimeWithoutSeconds(record.date_sent);
      }
    },
  },
  {
    title: '',
    dataindex: 'medsender_user',
    render: (text, record) => {
      const medsender_user = record.is_medsender_user
      return (
        medsender_user
          && (
          <Tooltip title={`This record was sent instantly by a Medsender user, providing you with the the ability to search through the record, and the ability to message the sender/recipient via Medsender Connect Chat!`} placement="left">
            <ThunderboltTwoTone twoToneColor="#691B9A"/>
          </Tooltip>
          )
      );
    },
  },
];

const sharedUploadedColumns = [
  {
    title: 'Date Uploaded',
    dataIndex: 'upload_date',
    render: (text, record) => (
      formatDateTimeWithoutSeconds(record.upload_date)
    ),
  },
];

const recordViewedIcon = (viewed, last_viewer) => (
  viewed ? (
    <>
      <Tooltip placement="topRight" title={`Record last viewed by ${last_viewer}`}>
        <EyeFilled style={{ paddingLeft: '5px' }} />
      </Tooltip>
    </>
  ) : <span style={{ marginLeft: '20px' }} />
);

// Derive up-to-two uppercase initials from an assignee display string.
// Parenthesized decorations are dropped ("Demo Bob (Synthetic)" -> "DB");
// punctuation inside real name words is stripped rather than disqualifying
// them ("Mary-Jane Smith" -> "MS", "O'Brien Smith" -> "OS"). Single word ->
// single initial; nothing alphabetic -> ''.
export const getAssigneeInitials = (assignee) => {
  if (!assignee) return '';
  const nameWords = assignee
    .trim()
    .split(/\s+/)
    .filter((word) => !/^[([{]/.test(word))
    .map((word) => word.replace(/[^a-zA-Z]/g, ''))
    .filter((word) => word.length > 0);
  if (nameWords.length === 0) return '';
  const initials = nameWords.length === 1
    ? nameWords[0][0]
    : nameWords[0][0] + nameWords[nameWords.length - 1][0];
  return initials.toUpperCase();
};

const assigneeIcon = (assignee) => {
  if (!assignee) {
    // Placeholder keeping the icon slot aligned with rows that do have an assignee
    return <span style={{ display: 'inline-block', width: '14px' }} />;
  }
  const initials = getAssigneeInitials(assignee);
  return (
    <Tooltip
      placement="topRight"
      title={`Assigned to ${assignee}`}
    >
      <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
        <UserOutlined />
        {initials && (
          <span style={{ fontSize: '0.6em', verticalAlign: 'super', marginLeft: '1px' }}>
            {initials}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

// Single-line container for the inbox icon cluster cells: keeps the icons on
// one row (the column is narrow enough that bare inline icons used to wrap and
// stack under each other) and spaces them evenly instead of per-icon margins.
const iconClusterStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  flexWrap: 'nowrap',
  whiteSpace: 'nowrap',
};

const showSenderEmail = record => (
  <>
    {record.receiver_email
      ? record.sender
      : <div>-</div>
    }
  </>
);

export const receivedInboxIcons = {
  title: '',
  dataIndex: 'patient_name',
  render: (text, record) => {
    const {
      can_edit_privacy_level: canEditPrivacyLevel,
      uri,
      viewed,
      download_count: downloadCount,
      is_uploaded_to_emr: isUploadedToEmr,
      tag,
      is_user_tagged_in_record: isUserTagged,
      send_token: sendToken,
    } = record;
    const last_viewer = record.hasOwnProperty('last_viewer') ? record.last_viewer : null;
    const assignee = record.hasOwnProperty('assignee') ? record.assignee : null;
    return (
      <span style={iconClusterStyle}>
        { tag && (
        <Tooltip title={`${tag} was notified`}>
          {isUserTagged
            ? <BellTwoTone />
            : <BellOutlined />
          }
        </Tooltip>
        )}
        { canEditPrivacyLevel && renderPrivacyLevel(record.privacy_level)}
        { record.archived && record.archived_by && renderArchivedBy(record.archived_by) }
        { uri && <Tooltip title="You've received a reply to a previously sent record"><MessageTwoTone /></Tooltip>}
        { recordViewedIcon(viewed, last_viewer) }
        { assigneeIcon(assignee) }
        { downloadCount > 0 && (
          <Tooltip title={`Record has been downloaded ${downloadCount} time(s)`} placement="topRight">
            <span style={{ whiteSpace: 'nowrap' }}>
              <DownloadOutlined />
              {downloadCount}
            </span>
          </Tooltip>
        )}
        { sendToken && <AutoSplitIcon sendToken={sendToken} record={record} /> }
      </span>
    );
  },
};

export const referredInboxIcons = {
  title: '',
  dataIndex: 'assigned_icon',
  render: (text, record) => {
    const assignee = record.hasOwnProperty('assignee') ? record.assignee : null;
    const sendToken = record.send_token;
    return (
      <span style={iconClusterStyle}>
        { record.archived && record.archived_by && renderArchivedBy(record.archived_by) }
        { assigneeIcon(assignee) }
        { sendToken && <AutoSplitIcon sendToken={sendToken} record={record} /> }
      </span>
    );
  },
};

export const chromeSelectorIcons = (inboxType) => {
  return {
    title: '',
    dataIndex: 'uploaded_icon',
    render: (text, record) => {
      const { is_uploaded_to_emr: isUploadedToEmr, viewed, last_viewer, send_token: sendToken, file_token: fileToken } = record;
      const sendTokenParam = sendToken
        ? `&sendToken=${sendToken}`
        : '';
      return (
        <>
          <Link
            to={{
              pathname: recordViewerPath(viaChromeExtension, fileToken),
              search: `?from=${inboxType}${sendTokenParam}`,
            }}
          >
            View Record
          </Link>
          {inboxType === 'received' && recordViewedIcon(viewed, last_viewer)}
          {isUploadedToEmr && (
            <Tooltip title="Record already uploaded to EMR" placement="left">
              <span style={{ margin: '10px' }}><ClipboardText size={18} weight="fill" /></span>
            </Tooltip>
          )}
        </>
      );
    },
  };
};

export const receivedColumns = [
  ...sharedColumnTitles('received'),
  ...sharedReceivedColumns,
  receivedInboxIcons,
];

export const assignedColumns = [
  ...sharedColumnTitles('assigned'),
  ...sharedAssignedColumns,
];

export const referredColumns = [
  ...sharedColumnTitles('referred'),
  ...sharedReferredColumns,
  referredInboxIcons,
];

const viaChromeExtension = true;

export const chromeReceivedColumns = [
  ...sharedColumnTitles('received', viaChromeExtension),
  ...sharedReceivedColumns,
  chromeSelectorIcons('received'),
];

export const chromeUploadedColumns = [
  ...sharedColumnTitles('uploaded', viaChromeExtension),
  ...sharedUploadedColumns,
  chromeSelectorIcons('uploaded'),
];

export const sentColumns = [
  ...sharedColumnTitles('sent'),
  {
    title: 'Recipient',
    dataIndex: 'receiver_email',
    // Since antd only supports one dataIndex value, but the column
    // could contain either of the below, we custom render the content
    render: (text, record) => (
      <>
        {record.receiver_name
          && (
            <>
              <span style={{ fontWeight: '500' }}>{record.receiver_name}</span>
              <br />
            </>
          )}
        {record.receiver_email || record.receiver_fax}
      </>
    ),
  },
  {
    title: 'From',
    dataIndex: 'sender',
    // Since antd only supports one dataIndex value, but the column
    // could contain either of the below, we custom render the content
    render: (text, record) => (
      <>
        {record.sender_name
            && (
              <>
                <span style={{ fontWeight: '500' }}>{record.sender_name}</span>
                <br />
              </>
            )}
        {validFaxNumber(record.sender).valid
          ? formatValidFaxNumber(record.sender)
          : showSenderEmail(record)
        }
      </>
    ),
  },
  {
    title: 'Date Sent',
    dataIndex: 'date_sent',
    render: (text, record) => (
      formatDateTimeWithoutSeconds(record.date_sent)
    ),
  },
  {
    title: '',
    dataIndex: 'view_status',
    render: (text, record) => {
      const patientQuery = record.patient ? '?patient=true' : '';
      const viewStatusIcon = record.patient ? getSentToPatientIcon(record) : getSentToProviderIcon(record);
      const viewSignatureIcon = getSignatureCompletedIcon(record);
      const isMedsenderUser = record.is_medsender_user;
      return (
        <>
          <Tooltip title={isMedsenderUser ? `Sending a record to a Medsender user guarantees instant delivery, gives recipients the ability to search through the record, and the ability to message the sender/recipient via Medsender Connect Chat!` :  ''} placement="left">
            <Link to={`/app/records/status/record/${record.file_token}/receiver/${record.send_token + patientQuery}`}>
              <Button type="primary" style={{ background: isMedsenderUser ? 'purple' : 'light blue', borderColor: isMedsenderUser ? 'purple' : 'light blue' }}>Status</Button>
            </Link>
          </Tooltip>
          
          <Tooltip title={viewStatusIcon.text}>
            <Icon component={viewStatusIcon.name} style={{ marginLeft: '20px', color: `${viewStatusIcon.color}`, fontSize: 17 }} />
          </Tooltip>
          {viewSignatureIcon && (
            <Tooltip title={viewSignatureIcon.text}>
              <Icon component={viewSignatureIcon.name} style={{ marginLeft: '20px', color: `${viewSignatureIcon.color}`, fontSize: 17 }} />
            </Tooltip>
          )}
        </>
      );
    },
  },
  {
    title: '',
    dataindex: 'sent_by',
    render: (text, record) => {
      const sender = record.sender || localStorage.getItem('m8_uid');
      return (
        record.sender !== localStorage.getItem('m8_uid')
          && (
          <Tooltip title={`This record was sent by ${sender}`} placement="left">
            <LinkOutlined />
          </Tooltip>
          )
      );
    },
  },
  {
    title: '',
    dataindex: 'medsender_user',
    render: (text, record) => {
      const medsender_user = record.is_medsender_user
      return (
        medsender_user
          && (
          <Tooltip title={`Sending a record to a Medsender user guarantees instant delivery, gives recipients the ability to search through the record, and the ability to message the sender/recipient via Medsender Connect Chat!`} placement="left">
            <ThunderboltTwoTone twoToneColor="#691B9A"/>
          </Tooltip>
          )
      );
    },
  },
];

export const uploadedColumns = [
  ...sharedColumnTitles('uploaded'),
  {
    title: 'Date Uploaded',
    dataIndex: 'upload_date',
    render: (text, record) => (
      formatDateTimeWithoutSeconds(record.upload_date)
    ),
  },
  {
    title: '',
    dataIndex: 'patient_name',
    render: (text, record) => {
      const archivedBy = record.archived_by ? record.archived_by : 'nil';
      return (
        <>
          {record.can_edit_privacy_level && renderPrivacyLevel(record.privacy_level)}
          <span style={{ marginLeft: '8px' }}>{(record.record_status === 'archived') && (archivedBy !== 'nil') && renderArchivedBy(archivedBy)}</span>
          <span style={{ marginLeft: '8px' }}>{ renderUploadedBy(record.uploaded_by) }</span>
        </>
      );
    },
  },
];
